const es = {
  header: {
    badge: "Guía Oficial de BotMix",
    title: "Formularios Web",
    summary:
      "Diseña formularios web interactivos para recolectar información estructurada de tus clientes en WhatsApp mediante Flow Builder. Cada campo captura las respuestas directamente en variables técnicas que reanudan de forma inmediata el flujo conversacional.",
  },
  sections: [
    {
      id: "gestion_formularios",
      title: "1. Creación y Estructura del Formulario",
      description:
        "Diferenciación entre el identificador técnico interno y la presentación visual para el cliente.",
      items: [
        {
          id: "titulo_formulario",
          title: "Título Visible al Cliente",
          definition:
            "El encabezado principal y formal que verá el cliente en su pantalla web (ej: 'Registro de Compra' o 'Formulario de Solicitud'). Admite formato libre con mayúsculas, tildes y nombres comerciales.",
        },
        {
          id: "nombre_tecnico",
          title: "Nombre Técnico (Identificador Interno)",
          definition:
            "Identificador interno del sistema para la gestión del formulario y generación del enlace/slug. Por regla técnica obligatoria, debe escribirse siempre en letras minúsculas, números y guiones bajos (ej: registro_compra), sin espacios, sin tildes ni caracteres especiales.",
        },
        {
          id: "descripcion_formulario",
          title: "Descripción e Instrucciones al Cliente",
          definition:
            "Texto explicativo que se muestra debajo del título en la tarjeta web. Ideal para orientar al cliente sobre el objetivo del formulario (ej: 'Por favor, completa los siguientes campos para poder finalizar con el registro de tu compra').",
        },
        {
          id: "slug_identificador",
          title: "Slug (Enlace Web Único)",
          definition:
            "Identificador alfanumérico que define la ruta web única del formulario (ej: /f/registro-compra-abc123). Puede personalizarse o generarse automáticamente.",
        },
      ],
    },
    {
      id: "campos_y_variables",
      title: "2. Campos, Variables Técnicas y Obligatoriedad",
      description:
        "Definición de tipos de entrada, asignación de variables de memoria y validación de campos requeridos.",
      items: [
        {
          id: "variables_tecnicas",
          title: "Variable / Key (Captura en Memoria)",
          definition:
            "Nombre técnico bajo el cual se guardará el dato en la memoria del bot. Debe crearse estrictamente en minúsculas y guiones bajos (ej: nombres, nro_documento, correo_cliente).",
        },
        {
          id: "etiqueta_publica",
          title: "Etiqueta Pública y Tipos de Campo",
          definition:
            "Texto visible sobre la casilla de llenado (ej: 'Nombres y Apellidos'). Puedes elegir entre Texto Corto, Texto Largo, Número, Email y Fecha.",
        },
        {
          id: "campos_requeridos",
          title: "Campos Requeridos (* Obligatorios)",
          definition:
            "Al marcar la casilla 'Req.', el campo se identificará con un asterisco rojo (*). El formulario web validará estos campos en tiempo real e impedirá el envío si falta algún dato requerido, mostrando la leyenda '* Campos obligatorios'.",
        },
      ],
    },
    {
      id: "integracion_flowbuilder",
      title: "3. Integración con Flow Builder y Flujos WhatsApp",
      description:
        "Cómo se vincula el formulario con las conversaciones y el procesamiento automático de variables.",
      items: [
        {
          id: "nodo_formulario",
          title: "Nodo 'Formulario Web' en Flow Builder",
          definition:
            "Dentro de Flow Builder (categoría Interacción), arrastra el nodo 'Formulario Web' y selecciona el formulario creado. La ventana mostrará automáticamente las variables capturadas para que puedas copiarlas con 1 clic.",
        },
        {
          id: "comodin_link",
          title: "Comodín {link} y Enlace Dinámico",
          definition:
            "Redacta el mensaje en el nodo incluyendo {link} (o {{link}}). BotMix generará de forma 100% automática la URL pública personalizada asociada al ticket del cliente en WhatsApp.",
        },
        {
          id: "inyeccion_variables",
          title: "Inyección de Datos y Reanudación Inmediata",
          definition:
            "Cuando el cliente pulsa 'Enviar y Continuar', todas sus respuestas se guardan en la memoria del flujo. El bot se reactiva al instante y continúa hacia el siguiente nodo (confirmación con {{variable}}, condiciones o transferencia a asesor).",
        },
      ],
    },
  ],
};

const en = {
  header: {
    badge: "BotMix Official Guide",
    title: "Web Forms",
    summary:
      "Design interactive web forms to collect structured customer information during WhatsApp flows in Flow Builder. Each field stores answers directly into technical variables that immediately resume the bot conversation.",
  },
  sections: [
    {
      id: "gestion_formularios",
      title: "1. Form Creation & Architecture",
      description:
        "Distinction between internal technical identifiers and customer-facing visual presentation.",
      items: [
        {
          id: "titulo_formulario",
          title: "Customer-Facing Title",
          definition:
            "The main formal header that customers see on their web screen (e.g., 'Purchase Registration' or 'Application Form'). Supports free formatting with capital letters, accents, and brand names.",
        },
        {
          id: "nombre_tecnico",
          title: "Technical Name (Internal ID)",
          definition:
            "Internal system identifier used for form management and URL slug generation. By strict rule, it must always be lowercase letters, numbers, and underscores (e.g., purchase_reg), with no spaces, accents, or special characters.",
        },
        {
          id: "descripcion_formulario",
          title: "Description & Customer Guidance",
          definition:
            "Instructive text displayed directly under the title in the form card. Perfect for guiding the customer (e.g., 'Please complete the following fields to finalize your order registration').",
        },
        {
          id: "slug_identificador",
          title: "Slug (Unique Web Link)",
          definition:
            "Alphanumeric identifier defining the unique web route of the form (e.g., /f/purchase-reg-abc123). Can be customized or auto-generated.",
        },
      ],
    },
    {
      id: "campos_y_variables",
      title: "2. Fields, Technical Variables & Required Rules",
      description:
        "Input types, memory variable assignment, and mandatory field validation.",
      items: [
        {
          id: "variables_tecnicas",
          title: "Variable / Key (Memory Capture)",
          definition:
            "Technical variable name where the answer is stored in bot memory. Strictly lowercase and underscores (e.g., names, id_number, customer_email).",
        },
        {
          id: "etiqueta_publica",
          title: "Public Label & Input Types",
          definition:
            "Visible label displayed on the input box (e.g., 'Full Name'). Choose from Short Text, Long Text, Number, Email, and Date.",
        },
        {
          id: "campos_requeridos",
          title: "Required Fields (* Mandatory)",
          definition:
            "Checking 'Req.' marks the field with a red asterisk (*). The web form validates required inputs in real time, blocks submission if incomplete, and displays '* Required fields'.",
        },
      ],
    },
    {
      id: "integracion_flowbuilder",
      title: "3. Flow Builder & WhatsApp Integration",
      description:
        "How forms link with WhatsApp conversations and process collected variables automatically.",
      items: [
        {
          id: "nodo_formulario",
          title: "'Web Form' Node in Flow Builder",
          definition:
            "In Flow Builder (Interaction category), add the 'Web Form' node and select your created form. The modal automatically displays all captured variables ready to copy with 1 click.",
        },
        {
          id: "comodin_link",
          title: "{link} Placeholder & Dynamic URLs",
          definition:
            "Write your message including {link} (or {{link}}). BotMix automatically substitutes it with the custom secure public link tied to the customer's WhatsApp ticket.",
        },
        {
          id: "inyeccion_variables",
          title: "Variable Injection & Instant Resumption",
          definition:
            "When the customer clicks 'Submit and Continue', answers are saved to flow memory. The bot resumes immediately and continues to the next node (confirmation with {{variable}}, conditions, or agent routing).",
        },
      ],
    },
  ],
};

const pt = {
  header: {
    badge: "Guia Oficial BotMix",
    title: "Formulários Web",
    summary:
      "Crie formulários web interativos para coletar dados estruturados de clientes durante conversas no WhatsApp com o Flow Builder. Cada campo alimenta variáveis técnicas que retomam imediatamente o fluxo conversacional.",
  },
  sections: [
    {
      id: "gestion_formularios",
      title: "1. Criação e Estrutura do Formulário",
      description:
        "Diferenciação entre o identificador técnico interno e a apresentação visual para o cliente.",
      items: [
        {
          id: "titulo_formulario",
          title: "Título Visível ao Cliente",
          definition:
            "O cabeçalho principal e formal que o cliente verá na tela web (ex: 'Registro de Compra' ou 'Formulário de Solicitação'). Permite maiúsculas, acentos e nomes comerciais.",
        },
        {
          id: "nombre_tecnico",
          title: "Nome Técnico (Identificador Interno)",
          definition:
            "Identificador interno do sistema para gestão e geração do link/slug. Por regra obrigatória, deve ser sempre em letras minúsculas, números e sublinhados (ex: registro_compra), sem espaços ou acentos.",
        },
        {
          id: "descripcion_formulario",
          title: "Descrição e Instruções ao Cliente",
          definition:
            "Texto explicativo exibido abaixo do título no cartão web. Ideal para orientar o cliente (ex: 'Por favor, preencha os seguintes campos para finalizar seu pedido').",
        },
        {
          id: "slug_identificador",
          title: "Slug (Link Web Único)",
          definition:
            "Identificador alfanumérico que define o caminho web único do formulário (ex: /f/registro-compra-abc123). Pode ser personalizado ou gerado automaticamente.",
        },
      ],
    },
    {
      id: "campos_y_variables",
      title: "2. Campos, Variáveis Técnicas e Obrigatoriedade",
      description:
        "Definição de tipos de entrada, atribuição de variáveis de memória e validação de campos obrigatórios.",
      items: [
        {
          id: "variables_tecnicas",
          title: "Variável / Key (Captura na Memória)",
          definition:
            "Nome técnico sob o qual a resposta é salva na memória do bot. Deve ser estritamente em minúsculas e sublinhados (ex: nomes, num_doc, email_cliente).",
        },
        {
          id: "etiqueta_publica",
          title: "Rótulo Público e Tipos de Campo",
          definition:
            "Texto visível sobre a caixa de preenchimento (ex: 'Nome Completo'). Escolha entre Texto Curto, Texto Longo, Número, E-mail e Data.",
        },
        {
          id: "campos_requeridos",
          title: "Campos Requeridos (* Obrigatórios)",
          definition:
            "Ao marcar 'Req.', o campo exibirá um asterisco vermelho (*). O formulário web valida em tempo real, bloqueia envios incompletos e mostra a legenda '* Campos obrigatórios'.",
        },
      ],
    },
    {
      id: "integracion_flowbuilder",
      title: "3. Integração com Flow Builder e WhatsApp",
      description:
        "Como vincular o formulário às conversas e processar as variáveis coletadas automaticamente.",
      items: [
        {
          id: "nodo_formulario",
          title: "Nó 'Formulário Web' no Flow Builder",
          definition:
            "No Flow Builder (categoria Interação), adicione o nó 'Formulário Web' e selecione o formulário criado. A janela exibe automaticamente as variáveis para copiar com 1 clique.",
        },
        {
          id: "comodin_link",
          title: "Marcador {link} e Link Dinâmico",
          definition:
            "Escreva a mensagem incluindo {link} (ou {{link}}). O BotMix gera automaticamente a URL segura personalizada vinculada ao ticket do cliente no WhatsApp.",
        },
        {
          id: "inyeccion_variables",
          title: "Injeção de Variáveis e Retomada Imediata",
          definition:
            "Ao clicar em 'Enviar e Continuar', as respostas são gravadas na memória do fluxo. O robô reativa imediatamente e continua para o próximo nó (confirmação com {{variavel}}, condições ou transferência).",
        },
      ],
    },
  ],
};

const fr = {
  header: {
    badge: "Guide Officiel BotMix",
    title: "Formulaires Web",
    summary:
      "Concevez des formulaires web interactifs pour recueillir des données structurées auprès de vos clients WhatsApp via Flow Builder. Chaque champ alimente des variables techniques qui relancent immédiatement la conversation du bot.",
  },
  sections: [
    {
      id: "gestion_formularios",
      title: "1. Création et Structure du Formulaire",
      description:
        "Différenciation entre l'identifiant technique interne et la présentation visuelle pour le client.",
      items: [
        {
          id: "titulo_formulario",
          title: "Titre Visible par le Client",
          definition:
            "L'en-tête formel visible par le client sur son écran web (ex: 'Enregistrement de Commande'). Format libre avec majuscules, accents et noms commerciaux.",
        },
        {
          id: "nombre_tecnico",
          title: "Nom Technique (Identifiant Interne)",
          definition:
            "Identifiant interne pour la gestion et la création du slug. Doit impérativement être composé de minuscules, chiffres et tirets bas (ex: enregistrement_commande), sans espaces ni accents.",
        },
        {
          id: "descripcion_formulario",
          title: "Description et Instructions Client",
          definition:
            "Texte explicatif placé sous le titre dans la carte web pour guider le client (ex: 'Veuillez remplir les champs suivants pour finaliser votre commande').",
        },
        {
          id: "slug_identificador",
          title: "Slug (Lien Web Unique)",
          definition:
            "Identifiant alphanumérique définissant l'URL web unique du formulaire (ex: /f/commande-abc123). Personnalisable ou généré automatiquement.",
        },
      ],
    },
    {
      id: "campos_y_variables",
      title: "2. Champs, Variables Techniques et Obligation",
      description:
        "Types d'entrées, attribution des variables de mémoire et validation des champs obligatoires.",
      items: [
        {
          id: "variables_tecnicas",
          title: "Variable / Clé (Capture en Mémoire)",
          definition:
            "Nom technique sous lequel la valeur est enregistrée dans le bot. Strictement en minuscules et tirets bas (ex: nom_client, numero_id, email).",
        },
        {
          id: "etiqueta_publica",
          title: "Libellé Public et Types de Champs",
          definition:
            "Texte affiché au-dessus de la zone de saisie (ex: 'Nom et Prénom'). Choix entre Texte Court, Texte Long, Nombre, Email et Date.",
        },
        {
          id: "campos_requeridos",
          title: "Champs Requis (* Obligatoires)",
          definition:
            "Cocher 'Req.' marque le champ d'un astérisque rouge (*). Le formulaire valide les saisies en direct, bloque l'envoi si incomplet et affiche la mention '* Champs obligatoires'.",
        },
      ],
    },
    {
      id: "integracion_flowbuilder",
      title: "3. Intégration Flow Builder et WhatsApp",
      description:
        "Liaison du formulaire aux flux conversationnels et traitement automatique des variables.",
      items: [
        {
          id: "nodo_formulario",
          title: "Nœud 'Formulaire Web' dans Flow Builder",
          definition:
            "Dans Flow Builder (catégorie Interaction), ajoutez le nœud 'Formulaire Web' et sélectionnez le formulaire créé. La boîte affiche immédiatement les variables prêtes à être copiées en 1 clic.",
        },
        {
          id: "comodin_link",
          title: "Balise {link} et Lien Dynamique",
          definition:
            "Rédigez votre message avec {link} (ou {{link}}). BotMix remplace la balise par l'URL publique personnalisée liée au ticket WhatsApp du client.",
        },
        {
          id: "inyeccion_variables",
          title: "Injection de Variables et Reprise Immédiate",
          definition:
            "Dès que le client clique sur 'Envoyer et Continuer', les réponses sont stockées en mémoire. Le bot reprend instantanément vers le nœud suivant (confirmation avec {{variable}}, conditions, etc.).",
        },
      ],
    },
  ],
};

const ar = {
  header: {
    badge: "دليل BotMix الرسمي",
    title: "نماذج الويب (Web Forms)",
    summary:
      "صمم نماذج ويب تفاعلية لجمع بيانات العملاء المنظمة أثناء تدفق محادثات واتساب في Flow Builder. يقوم كل حقل بتخزين الإجابات في متغيرات تقنية تستأنف المحادثة فوراً.",
  },
  sections: [
    {
      id: "gestion_formularios",
      title: "1. إنشاء وهيكلة النموذج",
      description:
        "التمييز بين المعرف التقني الداخلي والعرض المرئي المخصص للعميل.",
      items: [
        {
          id: "titulo_formulario",
          title: "العنوان الظاهر للعميل",
          definition:
            "العنوان الرئيسي الرسمي الذي يظهر للعميل في واجهة الويب (مثل: 'تسجيل طلب الشراء' أو 'نموذج الاستفسار'). يدعم الأحرف الكبيرة والرموز التجارية بحرية.",
        },
        {
          id: "nombre_tecnico",
          title: "الاسم التقني (المعرف الداخلي)",
          definition:
            "المعرف البرمجي الداخلي للنظام لإدارة النموذج وإنشاء الرابط (Slug). كقاعدة إلزامية، يجب كتابته دائماً بأحرف صغيرة وأرقام وشرطة سفلية (مثل: order_reg) دون مسافات أو رموز خاصة.",
        },
        {
          id: "descripcion_formulario",
          title: "الوصف والتعليمات الموجهة للعميل",
          definition:
            "نص توضيحي يظهر تحت العنوان داخل بطاقة الويب لإرشاد العميل (مثل: 'يرجى إكمال الحقول التالية لإتمام تسجيل طلبك بنجاح').",
        },
        {
          id: "slug_identificador",
          title: "الرابط المختصر المخصص (Slug)",
          definition:
            "المعرف الفريد الذي يحدد رابط الويب المخصص للنموذج (مثل: /f/order-reg-abc123). يمكن تخصيصه أو توليده تلقائياً.",
        },
      ],
    },
    {
      id: "campos_y_variables",
      title: "2. الحقول والمتغيرات التقنية وقواعد الإلزام",
      description:
        "أنواع المدخلات، تعيين متغيرات الذاكرة، والتحقق التلقائي من الحقول المطلوبة.",
      items: [
        {
          id: "variables_tecnicas",
          title: "المتغير / المفتاح البرمجي (الحفظ في الذاكرة)",
          definition:
            "الاسم البرمجي الذي يتم تخزين القيمة تحته في ذاكرة البوت. يُكتب بأحرف صغيرة وشرطة سفلية حصراً (مثل: full_name, doc_id, email).",
        },
        {
          id: "etiqueta_publica",
          title: "التسمية العامة وأنواع الحقول",
          definition:
            "النص الظاهر فوق خانة الإدخال (مثل: 'الاسم الكامل'). اختر بين نص قصير، نص طويل، رقم، بريد إلكتروني، وتاريخ.",
        },
        {
          id: "campos_requeridos",
          title: "الحقول المطلوبة (* إلزامية)",
          definition:
            "عند تفعيل 'Req.'، يظهر الحقل بعلامة نجمة حمراء (*). يتحقق النموذج من تعبئتها فوراً ويمنع الإرسال إذا كانت فارغة مع عرض إشعار '* حقول إلزامية'.",
        },
      ],
    },
    {
      id: "integracion_flowbuilder",
      title: "3. التكامل مع Flow Builder ومحادثات واتساب",
      description:
        "ربط النموذج بالمحادثة ومعالجة المتغيرات واستئناف التدفق التلقائي.",
      items: [
        {
          id: "nodo_formulario",
          title: "عقدة 'نموذج ويب' في Flow Builder",
          definition:
            "ضمن Flow Builder (قسم التفاعل)، اسحب عقدة 'نموذج ويب' واختر النموذج. تعرض النافذة المتغيرات المحفوظة جاهزة للنسخ بنقرة واحدة.",
        },
        {
          id: "comodin_link",
          title: "الرمز {link} والرابط الديناميكي",
          definition:
            "اكتب نص الرسالة متضمناً {link} (أو {{link}})، وسيقوم BotMix تلقائياً باستبداله برابط ويب آمن فريد لتذكرة محادثة العميل.",
        },
        {
          id: "inyeccion_variables",
          title: "تخزين المتغيرات والاستئناف الفوري",
          definition:
            "عند ضغط العميل على 'إرسال ومتابعة'، تُحفظ الإجابات في ذاكرة التدفق. يستأنف البوت فوراً عبر موصل الإخراج نحو العقدة التالية (تأكيد بمتغيرات {{variable}}، شروط أو تحويل).",
        },
      ],
    },
  ],
};

export const FORMS_HELP_DATA = { es, en, pt, fr, ar };
export default FORMS_HELP_DATA;
